import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Check, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useMembership } from "@/hooks/useMembership";
import { isoDay, monthName, themeForMonth } from "@/data/self-care-calendar";
import { listMonthCompletions, momentsLabel, setDayComplete } from "@/lib/self-care-calendar";

export const Route = createFileRoute("/self-care-calendar")({
  head: () => ({
    meta: [
      { title: "Monthly Self-Care Calendar — Recovery Trauma™" },
      {
        name: "description",
        content: "One small act of care each day. No pressure, no streaks, no catching up.",
      },
      { property: "og:title", content: "Monthly Self-Care Calendar — Recovery Trauma™" },
      {
        property: "og:description",
        content: "A gentle monthly calendar of small self-care moments for members.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: SelfCareCalendarPage,
});

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function SelfCareCalendarPage() {
  const { isMember, isAdmin, isAuthed } = useMembership();
  const enabled = isAuthed && (isMember || isAdmin);
  const [today] = useState(() => new Date());
  const [view, setView] = useState(() => ({ y: today.getFullYear(), m: today.getMonth() + 1 }));
  const [done, setDone] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<number | null>(null);
  const theme = themeForMonth(view.y, view.m);
  const daysInMonth = new Date(view.y, view.m, 0).getDate();
  const offset = (new Date(view.y, view.m - 1, 1).getDay() + 6) % 7;
  const todayIso = isoDay(today);

  useEffect(() => {
    if (!enabled) return;
    listMonthCompletions(view.y, view.m).then(setDone);
  }, [enabled, view.y, view.m]);

  const cells = useMemo(
    () => [...Array(offset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)],
    [offset, daysInMonth],
  );

  const shift = (d: number) => {
    setSelected(null);
    setView((v) => {
      const date = new Date(v.y, v.m - 1 + d, 1);
      return { y: date.getFullYear(), m: date.getMonth() + 1 };
    });
  };

  const dayIso = (d: number) => isoDay(new Date(view.y, view.m - 1, d));
  const toggle = async (d: number) => {
    const iso = dayIso(d);
    const was = done.has(iso);
    const next = new Set(done);
    if (was) next.delete(iso);
    else next.add(iso);
    setDone(next);
    const res = await setDayComplete(iso, theme.key, !was);
    if (!res.ok) setDone(done);
  };

  const sel = selected ? theme.acts[(selected - 1) % theme.acts.length]! : null;
  const selIso = selected ? dayIso(selected) : "";
  const selFuture = selected ? selIso > todayIso : false;

  return (
    <div className="min-h-dvh bg-gradient-to-b from-background to-[var(--gold-soft)]/25 px-4 pb-28 pt-6">
      <div className="mx-auto max-w-3xl">
        <header className="text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--gold-deep)]">
            Monthly self-care
          </p>
          <h1 className="mt-1 font-display text-3xl text-foreground sm:text-4xl">
            {theme.emoji} {monthName(view.m)} {theme.name}
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">{theme.tagline}</p>
          {enabled ? (
            <p className="mt-3 inline-block rounded-full border border-gold/30 bg-card px-3 py-1 text-xs font-medium text-foreground/80">
              {momentsLabel(done.size)}
            </p>
          ) : (
            <p className="mt-3 text-xs text-muted-foreground">
              <Link to="/auth" className="font-semibold text-[var(--gold-deep)]">
                Sign in
              </Link>{" "}
              as a member to save your moments.
            </p>
          )}
        </header>

        <div className="mt-5 flex items-center justify-between">
          <button type="button" onClick={() => shift(-1)} aria-label="Previous month" className="grid h-10 w-10 place-items-center rounded-full border border-gold/30 bg-card">
            <ChevronLeft className="h-5 w-5 text-[var(--gold-deep)]" />
          </button>
          <span className="font-display text-lg text-foreground">
            {monthName(view.m)} {view.y}
          </span>
          <button type="button" onClick={() => shift(1)} aria-label="Next month" className="grid h-10 w-10 place-items-center rounded-full border border-gold/30 bg-card">
            <ChevronRight className="h-5 w-5 text-[var(--gold-deep)]" />
          </button>
        </div>

        <div className="mt-3 grid grid-cols-7 gap-1 sm:gap-2" role="grid">
          {WEEKDAYS.map((w) => (
            <div key={w} className="rounded-md bg-[var(--gold-deep)]/85 py-1 text-center text-[10px] font-semibold uppercase text-primary-foreground sm:text-xs">
              {w}
            </div>
          ))}
          {cells.map((d, i) => {
            if (!d) return <div key={`e${i}`} />;
            const iso = dayIso(d);
            const act = theme.acts[(d - 1) % theme.acts.length]!;
            const isDone = done.has(iso);
            const isToday = iso === todayIso;
            return (
              <button
                key={iso}
                type="button"
                onClick={() => setSelected(d)}
                aria-label={`${d} ${monthName(view.m)}: ${act.title}${isDone ? ", completed" : ""}`}
                className={`relative flex aspect-square min-w-0 flex-col items-center justify-center rounded-lg border p-0.5 text-center transition sm:aspect-[4/5] sm:items-start sm:justify-start sm:p-2 sm:text-start ${
                  isToday ? "border-[var(--gold-deep)] ring-2 ring-[var(--gold-deep)]/30" : "border-gold/25"
                } ${isDone ? "bg-[var(--gold-soft)]/70" : "bg-card"}`}
              >
                <span className="text-[11px] font-bold text-foreground sm:text-sm">{d}</span>
                <span aria-hidden className="text-base leading-none sm:mt-1 sm:text-2xl">{act.emoji}</span>
                <span className="mt-1 hidden text-[11px] leading-tight text-foreground/75 sm:line-clamp-3">
                  {act.title}
                </span>
                {isDone ? (
                  <Check className="absolute right-0.5 top-0.5 h-3 w-3 text-[var(--gold-deep)] sm:h-4 sm:w-4" aria-hidden />
                ) : null}
              </button>
            );
          })}
        </div>

        <p className="mt-4 text-center text-xs text-muted-foreground">
          Missing a day never resets anything. Come back whenever it feels right.
        </p>
      </div>

      {selected && sel ? (
        <div className="fixed inset-x-0 bottom-0 z-50 px-3 pb-[calc(env(safe-area-inset-bottom)+5rem)] sm:pb-6">
          <div role="dialog" aria-label={`${selected} ${monthName(view.m)}`} className="mx-auto max-w-md rounded-2xl border border-gold/30 bg-card p-4 shadow-xl">
            <div className="flex items-start justify-between gap-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[var(--gold-deep)]">
                {selected} {monthName(view.m)}
              </p>
              <button type="button" onClick={() => setSelected(null)} aria-label="Close" className="grid h-9 w-9 place-items-center rounded-full hover:bg-accent/40">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-base font-semibold text-foreground">
              <span aria-hidden>{sel.emoji}</span> {sel.title}
            </p>
            {enabled ? (
              selFuture ? (
                <p className="mt-2 text-xs text-muted-foreground">This one opens on the day. No rush.</p>
              ) : (
                <button
                  type="button"
                  onClick={() => toggle(selected)}
                  className={`mt-3 inline-flex min-h-[44px] items-center gap-1.5 rounded-full px-5 text-sm font-semibold ${
                    done.has(selIso)
                      ? "border border-gold/40 text-[var(--gold-deep)]"
                      : "bg-[var(--gold-deep)] text-primary-foreground"
                  }`}
                >
                  <Check className="h-4 w-4" aria-hidden />
                  {done.has(selIso) ? "Completed — tap to undo" : "Mark as complete"}
                </button>
              )
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
